import { styled } from '@mui/material/styles';
import { MINT_MIST } from '../../constants/brandSurfaces';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';
const deepTeal = '#047857';
const danger = '#c62828';

/* ─── Full page (/contact) ─── */
export const ContactPanel = styled('section')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 6fr)',
  alignItems: 'start',
  gap: 40,
  margin: '12px 0 48px',
  padding: '44px 48px',
  borderRadius: 24,
  background: MINT_MIST,
  border: '1px solid rgba(26, 26, 46, 0.06)',
  textAlign: 'left',
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr',
    gap: 24,
    padding: '32px 28px',
  },
  '@media (max-width: 600px)': {
    margin: '8px 0 32px',
    padding: '22px 12px',
    borderRadius: 18,
  },
});

export const Intro = styled('div')({
  paddingTop: 8,
  '@media (max-width: 900px)': { paddingTop: 0 },
});

export const Eyebrow = styled('p')({
  margin: '0 0 8px',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: blue,
});

export const Heading = styled('h1')({
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: '2.2rem',
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 10px',
  '@media (max-width: 600px)': {
    fontSize: '1.65rem',
  },
});

export const Lead = styled('p')({
  fontSize: '1.05rem',
  lineHeight: 1.55,
  color: muted,
  margin: '0 0 20px',
  maxWidth: 440,
});

export const InfoList = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  '& li': {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 14,
    background: '#ffffff',
    border: '1px solid rgba(26, 26, 46, 0.06)',
    fontSize: '0.95rem',
    color: muted,
  },
  '& strong': {
    display: 'block',
    color: ink,
    fontWeight: 700,
  },
});

export const InfoIcon = styled('span')({
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 38,
  height: 38,
  borderRadius: '50%',
  color: blue,
  background: 'rgba(1, 235, 178, 0.18)',
  '& svg': { fontSize: 20 },
});

export const FormCard = styled('div')({
  padding: '28px 28px 24px',
  borderRadius: 20,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.06)',
  boxShadow: '0 8px 28px rgba(26, 26, 46, 0.08)',
  '@media (max-width: 600px)': {
    padding: '20px 16px 18px',
    borderRadius: 16,
  },
});

/* ─── Compact card (launch page) ─── */
export const EmbeddedFormCard = styled('div')({
  padding: '20px 18px 18px',
  borderRadius: 12,
  background: '#f7fafc',
  border: '1px solid rgba(26, 26, 46, 0.08)',
  '@media (max-width: 600px)': {
    padding: '16px 14px 14px',
  },
});

/* ─── Form fields ─── */
export const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
});

export const FieldRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 14,
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
});

export const FieldLabel = styled('label')({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: '0.92rem',
  fontWeight: 700,
  color: ink,
});

const inputBase = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  borderRadius: 10,
  border: '1.5px solid rgba(26, 26, 46, 0.12)',
  background: '#ffffff',
  fontFamily: 'inherit',
  fontSize: '1rem',
  fontWeight: 400,
  color: ink,
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&::placeholder': { color: '#8a93a6' },
  '&:focus': {
    outline: 'none',
    borderColor: blue,
    boxShadow: '0 0 0 3px rgba(13, 78, 254, 0.12)',
  },
  '&[aria-invalid="true"]': { borderColor: danger },
};

export const FieldInput = styled('input')(inputBase);

export const FieldTextarea = styled('textarea')({
  ...inputBase,
  minHeight: 130,
  lineHeight: 1.55,
  resize: 'vertical',
});

export const FieldError = styled('span')({
  fontSize: '0.85rem',
  fontWeight: 400,
  color: danger,
});

export const SignedInNote = styled('p')({
  margin: 0,
  padding: '10px 14px',
  borderRadius: 10,
  fontSize: '0.92rem',
  lineHeight: 1.5,
  color: muted,
  background: MINT_MIST,
});

export const SubmitError = styled('div')({
  padding: '12px 14px',
  borderRadius: 10,
  fontSize: '0.92rem',
  lineHeight: 1.45,
  color: '#b71c1c',
  background: 'rgba(211, 47, 47, 0.08)',
  border: '1px solid rgba(211, 47, 47, 0.3)',
});

export const SubmitButton = styled('button', {
  shouldForwardProp: (prop) => prop !== '$compact',
})(({ $compact }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: $compact ? 'auto' : '100%',
  minWidth: $compact ? 220 : undefined,
  alignSelf: $compact ? 'center' : 'stretch',
  minHeight: 46,
  marginTop: 4,
  padding: '12px 24px',
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '1rem',
  fontWeight: 700,
  color: '#ffffff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  transition: 'filter 0.15s ease, box-shadow 0.15s ease',
  '&:hover:not(:disabled)': {
    filter: 'brightness(1.05)',
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.4)',
  },
  '&:focus-visible': {
    outline: '3px solid rgba(13, 78, 254, 0.35)',
    outlineOffset: 2,
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
}));

export const FormFootnote = styled('p')({
  margin: 0,
  fontSize: '0.85rem',
  color: muted,
  textAlign: 'center',
});

/* ─── Success ─── */
export const SuccessBox = styled('div')({
  textAlign: 'center',
  padding: '12px 4px',
});

export const SuccessIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  marginBottom: 12,
  borderRadius: '50%',
  color: deepTeal,
  background: 'rgba(1, 235, 178, 0.18)',
  '& svg': { fontSize: 30 },
});

export const SuccessTitle = styled('h2')({
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: '1.3rem',
  fontWeight: 700,
  color: ink,
  margin: '0 0 6px',
});

export const SuccessText = styled('p')({
  fontSize: '0.98rem',
  lineHeight: 1.55,
  color: muted,
  margin: '0 auto 16px',
  maxWidth: 380,
});

export const HomeLink = styled('a')({
  fontWeight: 600,
  color: blue,
  textDecoration: 'none',
  '&:hover, &:focus-visible': {
    textDecoration: 'underline',
    textUnderlineOffset: 3,
  },
});
